import React from 'react';
import {
  MapPin,
  Globe,
  Clock,
  Car,
  Wifi,
  WifiOff,
  Users,
  Languages,
  Eye,
  Navigation,
  Home,
} from 'lucide-react';
import { SectionCard, InfoRow, MetricCard, TabLoading, TabError, TabEmpty, StatusBadge } from './shared';
import { useUserLocation } from '../../hooks/useUserTabs';
import { formatDateTime, formatRelativeTime } from '../../utils/helpers';

interface LocationTabProps {
  userId: number;
}

function formatCoord(val: number | null): string {
  if (val === null || val === undefined) return 'N/A';
  return val.toFixed(6);
}

function formatAddress(data: { address: string | null; city: string | null; state: string | null; zipCode: string | null; country: string | null }): string | null {
  const parts = [data.address, data.city, data.state, data.zipCode, data.country].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
}

function kmToMiles(km: number): string {
  return (km * 0.621371).toFixed(1);
}

const LocationTab: React.FC<LocationTabProps> = ({ userId }) => {
  const { data, loading, error, refetch } = useUserLocation(userId, true);

  if (loading) return <TabLoading />;
  if (error) return <TabError message={error} onRetry={refetch} />;
  if (!data) return <TabEmpty message="No location data available" />;

  const fullAddress = formatAddress(data);
  const travelRadius = data.travelRadiusKm;

  return (
    <div className="space-y-5">
      {/* Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          label="Status"
          value={data.isOnline ? 'Online' : 'Offline'}
          icon={data.isOnline ? Wifi : WifiOff}
          color={data.isOnline ? 'text-emerald-400' : 'text-gray-400'}
          bgColor={data.isOnline ? 'bg-emerald-500/10' : 'bg-gray-500/10'}
        />
        <MetricCard
          label="Timezone"
          value={data.timezone || 'N/A'}
          icon={Clock}
          color="text-amber-400"
          bgColor="bg-amber-500/10"
        />
        <MetricCard
          label="Travels to Customer"
          value={data.travelsToCustomer ? 'Yes' : 'No'}
          icon={Car}
          color={data.travelsToCustomer ? 'text-blue-400' : 'text-gray-400'}
          bgColor={data.travelsToCustomer ? 'bg-blue-500/10' : 'bg-gray-500/10'}
        />
        <MetricCard
          label="Travel Radius"
          value={travelRadius != null ? `${travelRadius.toFixed(1)} km` : 'N/A'}
          icon={Navigation}
          color="text-violet-400"
          bgColor="bg-violet-500/10"
          subtitle={travelRadius != null ? `${kmToMiles(travelRadius)} mi` : undefined}
        />
      </div>

      {/* Address */}
      {fullAddress && (
        <div className="flex items-start gap-3 px-4 py-3 rounded-lg border border-border/50 bg-muted/10">
          <Home className="h-4 w-4 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-foreground">{fullAddress}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">Registered address</p>
          </div>
        </div>
      )}

      {/* Physical Location */}
      <SectionCard title="Physical Location" icon={MapPin} subtitle="Home / registered coordinates">
        <div className="space-y-0">
          {data.address && <InfoRow label="Address" value={data.address} icon={Home} />}
          {data.city && <InfoRow label="City" value={data.city} icon={MapPin} />}
          {data.state && <InfoRow label="State" value={data.state} icon={MapPin} />}
          {data.zipCode && <InfoRow label="Zip Code" value={data.zipCode} icon={MapPin} mono />}
          {data.country && <InfoRow label="Country" value={data.country} icon={Globe} />}
          <InfoRow label="Latitude" value={formatCoord(data.latitude)} icon={MapPin} mono />
          <InfoRow label="Longitude" value={formatCoord(data.longitude)} icon={MapPin} mono />
          {data.latitude != null && data.longitude != null && (
            <InfoRow
              label="Map"
              value={
                <a
                  href={`https://www.google.com/maps?q=${data.latitude},${data.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary/80 text-xs underline"
                >
                  Open in Google Maps
                </a>
              }
              icon={Globe}
            />
          )}
        </div>
      </SectionCard>

      {/* Online Location */}
      <SectionCard title="Online Location" icon={Wifi} subtitle="Last known online coordinates">
        <div className="space-y-0">
          <InfoRow label="Online Latitude" value={formatCoord(data.onlineLatitude)} icon={Globe} mono />
          <InfoRow label="Online Longitude" value={formatCoord(data.onlineLongitude)} icon={Globe} mono />
          {data.onlineLocationUpdatedAt && (
            <InfoRow
              label="Location Updated"
              value={
                <span title={formatDateTime(data.onlineLocationUpdatedAt)}>
                  {formatRelativeTime(data.onlineLocationUpdatedAt)}
                  <span className="text-muted-foreground/60 ml-1.5 text-[10px]">
                    ({formatDateTime(data.onlineLocationUpdatedAt)})
                  </span>
                </span>
              }
              icon={Clock}
            />
          )}
          {data.onlineLatitude != null && data.onlineLongitude != null && (
            <InfoRow
              label="Map"
              value={
                <a
                  href={`https://www.google.com/maps?q=${data.onlineLatitude},${data.onlineLongitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary/80 text-xs underline"
                >
                  Open in Google Maps
                </a>
              }
              icon={Globe}
            />
          )}
        </div>
      </SectionCard>

      {/* Availability & Activity */}
      <SectionCard title="Availability & Activity" icon={Eye}>
        <div className="space-y-0">
          <InfoRow
            label="Online Status"
            value={
              <StatusBadge
                status={data.isOnline ? 'Online' : 'Offline'}
                variant={data.isOnline ? 'success' : 'default'}
              />
            }
            icon={data.isOnline ? Wifi : WifiOff}
          />
          <InfoRow
            label="Online Until"
            value={data.onlineUntil ? formatDateTime(data.onlineUntil) : 'No limit set'}
            icon={Clock}
          />
          {data.lastSeenAt && (
            <InfoRow
              label="Last Seen"
              value={
                <span title={formatDateTime(data.lastSeenAt)}>
                  {formatRelativeTime(data.lastSeenAt)}
                  <span className="text-muted-foreground/60 ml-1.5 text-[10px]">
                    ({formatDateTime(data.lastSeenAt)})
                  </span>
                </span>
              }
              icon={Eye}
            />
          )}
          <InfoRow
            label="Travels to Customer"
            value={
              <StatusBadge
                status={data.travelsToCustomer ? 'Yes' : 'No'}
                variant={data.travelsToCustomer ? 'success' : 'default'}
              />
            }
            icon={Car}
          />
          <InfoRow
            label="Travel Radius"
            value={
              travelRadius != null
                ? `${travelRadius.toFixed(1)} km (${kmToMiles(travelRadius)} mi)`
                : null
            }
            icon={Navigation}
          />
          <InfoRow
            label="Timezone"
            value={data.timezone}
            icon={Clock}
          />
          <InfoRow
            label="Platform"
            value={data.platform}
            icon={Globe}
          />
        </div>
      </SectionCard>

      {/* Demographics & Languages */}
      {(data.ethnicity || data.languages.length > 0 || data.preferredLanguages.length > 0) && (
        <SectionCard title="Demographics & Languages" icon={Users}>
          <div className="space-y-0">
            <InfoRow label="Ethnicity" value={data.ethnicity} icon={Users} />
            <InfoRow
              label="Languages"
              value={data.languages.length > 0 ? data.languages.join(', ') : null}
              icon={Languages}
            />
            <InfoRow
              label="Preferred Languages"
              value={data.preferredLanguages.length > 0 ? data.preferredLanguages.join(', ') : null}
              icon={Languages}
            />
          </div>
        </SectionCard>
      )}
    </div>
  );
};

export default LocationTab;
